import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AdminService } from '../admin.service';
import { SnackbarService } from 'src/app/core/snackbar.service';
import { Provider } from '@models/mood/general/provider';
import { v4 } from 'uuid';

interface LogoSubForm {
  logo_id: FormControl<string | null>;
  logo_name: FormControl<string | null>;
  logo_base64: FormControl<string | null>;
  logo_size: FormControl<number | null>;
}

interface ProviderForm {
  name: FormControl<string | null>;

  logo: FormGroup<LogoSubForm>;
}

interface FileControl {
  _files: File[]
}

@Component({
  selector: 'mood-provider-edit-form',
  templateUrl: './provider-edit-form.component.html',
  styleUrls: [
    'provider-edit-form.component.scss'
  ]
})
export class ProviderEditFormComponent implements OnInit {
  private _providerId!: string;
  @Input() set providerId(value: string) {
    this._providerId = value;

    this._loadAndPatchExistingData();
  };

  public get providerId() {
    return this._providerId;
  }

  providerForm: FormGroup<ProviderForm>;

  logoControl = new FormControl<FileControl | null>(null);
  currentProvider?: Provider;


  constructor(
    private _snackbarService: SnackbarService,
    private _changeRef: ChangeDetectorRef,
    private _adminService: AdminService) {

    this.providerForm = new FormGroup<ProviderForm>({
      name: new FormControl<string | null>(null, [Validators.required]),
      logo: new FormGroup<LogoSubForm>({
        logo_id: new FormControl<string | null>(null),
        logo_name: new FormControl<string | null>(null),
        logo_base64: new FormControl<string | null>(null),
        logo_size: new FormControl<number | null>(null)
      })
    });
  }

  private async _loadAndPatchExistingData() {
    this.providerForm.reset();

    this.currentProvider = (await this._adminService.findProviderById(this.providerId));

    if (this.currentProvider) {
      this.providerForm.patchValue({
        name: this.currentProvider?.name ?? null,
        logo:{
          logo_id: this.currentProvider?.logo_id ?? null,
          logo_name: this.currentProvider?.logo_name ?? null,
          logo_base64: this.currentProvider?.logo_base64 ?? null,
          logo_size: this.currentProvider?.logo_size ?? null,
        }
      });

      this.providerForm.markAsPristine();
    }
  }

  get saveButtonDisabled() {
    return this.providerForm.invalid || this.providerForm.pristine;
  }

  public get logoGroup() {
    return this.providerForm.get('logo') as FormGroup<LogoSubForm>;
  }

  async ngOnInit() {
    this.logoControl.valueChanges.subscribe(result => {
      if (!result) {
        this.logoGroup.reset();
        if (this.currentProvider?.logo_base64) {
          this.logoGroup.patchValue({
            logo_id: this.currentProvider?.logo_id,
            logo_name: this.currentProvider?.logo_name,
            logo_base64: this.currentProvider?.logo_base64,
            logo_size: this.currentProvider?.logo_size
          });
        }
      } else {
        const reader = new FileReader();
        reader.onloadend = () => {
          this.logoGroup.patchValue({
            logo_id: v4(),
            logo_size: result._files[0].size,
            logo_name: result._files[0].name,
            logo_base64: reader.result?.toString()!
          });

          this.logoGroup.markAsDirty();
          this._changeRef.markForCheck();
        };

        reader.readAsDataURL(result._files[0]);
      }
    })
  }

  public get logoSource() {
    return this.logoGroup?.get('logo_base64')?.value;
  }

  public clearNewLogoField() {
    this.logoControl.reset();
  }

  public removeExistingLogo() {
    this.logoGroup.reset();
    this.logoGroup.markAsDirty();
  }

  public get newLogoControlPristine() {
    return this.logoControl.pristine;
  }

  public get hasExistingLogo() {
    return this.logoGroup.get('logo_name')?.value && (this.logoGroup.get('logo_name')?.value === this.currentProvider?.logo_name);
  }

  async saveForm() {
    // TODO: Handle failure
    if (this.providerForm.valid) {
      const rawValue = this.providerForm.getRawValue();
      const provider: Provider = {
        id: this.providerId,
        name: rawValue?.name!,
        logo_id: rawValue?.logo?.logo_id,
        logo_name: rawValue?.logo?.logo_name,
        logo_base64: rawValue?.logo?.logo_base64,
        logo_size: rawValue?.logo?.logo_size
      }
      this.currentProvider = await this._adminService.updateProvider(this.providerId, {
        ...provider as Provider,
      });
      await this._adminService.updateProviderById(this.providerId, this.currentProvider);

      this._adminService.providerUpdated.emit();

      this.logoControl.reset(null, { emitEvent: false });
      this.providerForm.markAsPristine();

      this._snackbarService.openSuccessMessage("Provider updated");

      this._changeRef.markForCheck();
    }
  }
}
